import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { reservationsAPI } from '../services/api';
import './OrderDetails.css';

const STATUS_LABELS = {
  reserved_pending_deposit: 'Awaiting deposit payment',
  reserved_paid_deposit: 'Deposit paid — ready to complete',
  completed: 'Completed',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

const formatMoney = (amount, currency = 'AED') =>
  `${currency} ${Number(amount || 0).toFixed(2)}`;

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '—');

function DetailSection({ title, children, className = '' }) {
  return (
    <div className={`order-detail__panel ${className}`.trim()}>
      <h3 className="order-detail__section-title">{title}</h3>
      {children}
    </div>
  );
}

function DetailRows({ rows }) {
  return (
    <dl className="order-detail__dl">
      {rows.map(([dt, dd]) => (
        <div key={dt} className="order-detail__row">
          <dt className="order-detail__dt">{dt}</dt>
          <dd className="order-detail__dd">{dd}</dd>
        </div>
      ))}
    </dl>
  );
}

const ReservationDetails = ({ reservation, onUpdated, onDeleted, readOnly = false }) => {
  const navigate = useNavigate();
  const [note, setNote] = useState('');
  const [completing, setCompleting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!reservation) return null;

  const buyerName = [reservation.buyer?.first_name, reservation.buyer?.last_name]
    .filter(Boolean)
    .join(' ');

  const statusLabel = STATUS_LABELS[reservation.status] || reservation.status;

  const handleComplete = async () => {
    if (
      !window.confirm(
        'Mark this reservation as fully paid and convert it to a confirmed order? The listing will be marked sold.',
      )
    ) {
      return;
    }
    setCompleting(true);
    try {
      const response = await reservationsAPI.complete(reservation.id, {
        note: note.trim() || undefined,
      });
      toast.success(response.data.message || 'Reservation completed.');
      const orderId = response.data.order?.id;
      if (typeof onUpdated === 'function') {
        onUpdated(response.data.reservation);
      }
      if (orderId) {
        navigate(`/orders/${orderId}`);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || error.message || 'Could not complete reservation',
      );
    } finally {
      setCompleting(false);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        'Permanently delete this reservation? Active holds will be released and the listing may become available again.',
      )
    ) {
      return;
    }

    setDeleting(true);
    try {
      await reservationsAPI.delete(reservation.id);
      toast.success('Reservation deleted.');
      if (typeof onDeleted === 'function') {
        onDeleted();
      } else {
        navigate('/reservations');
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || error.message || 'Could not delete reservation',
      );
    } finally {
      setDeleting(false);
    }
  };

  const timelineRows = [
    ['Created', formatDate(reservation.created_at)],
    ['Expires', formatDate(reservation.expires_at)],
    ...(reservation.deposit_paid_at
      ? [['Deposit paid', formatDate(reservation.deposit_paid_at)]]
      : []),
    ...(reservation.completed_at
      ? [['Completed', formatDate(reservation.completed_at)]]
      : []),
    ...(reservation.cancelled_at
      ? [['Cancelled', formatDate(reservation.cancelled_at)]]
      : []),
  ];

  const amountRows = [
    ['Total', formatMoney(reservation.total_amount, reservation.currency)],
    [
      `Deposit (${reservation.deposit_percent}%)`,
      formatMoney(reservation.deposit_amount, reservation.currency),
    ],
    ['Balance due', formatMoney(reservation.balance_amount, reservation.currency)],
    ['Quantity', reservation.quantity],
  ];

  const buyerRows = [
    ['Name', buyerName || '—'],
    ['Email', reservation.buyer?.email || '—'],
  ];

  const address = reservation.shipping_address;
  const addressRows = address
    ? [
        ['Name', address.full_name || '—'],
        ['Phone', address.phone_number || '—'],
        [
          'Address',
          [address.address_line1, address.city, address.country]
            .filter(Boolean)
            .join(', ') || '—',
        ],
      ]
    : [];

  return (
    <div className="order-detail">
      <div
        className="order-detail__panel"
        style={{
          marginBottom: '1.25rem',
          display: 'flex',
          gap: '1.25rem',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        {reservation.product?.image_url ? (
          <img
            src={reservation.product.image_url}
            alt=""
            className="order-detail__product-thumb"
            style={{ width: 120, height: 120 }}
          />
        ) : null}
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Reservation
          </div>
          <h2 style={{ margin: '6px 0 8px', fontSize: '1.35rem', lineHeight: 1.3 }}>
            {reservation.product?.title || 'Product'}
          </h2>
          {reservation.product?.brand ? (
            <div style={{ color: '#64748b', marginBottom: '10px' }}>{reservation.product.brand}</div>
          ) : null}
          <span className={`status-badge status-${reservation.status}`} style={{ textTransform: 'none' }}>
            {statusLabel}
          </span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '12px', color: '#64748b' }}>Deposit</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>
            {formatMoney(reservation.deposit_amount, reservation.currency)}
          </div>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px' }}>Balance</div>
          <div style={{ fontWeight: 600 }}>
            {formatMoney(reservation.balance_amount, reservation.currency)}
          </div>
        </div>
      </div>

      <div className="order-detail__grid">
        <DetailSection title="Timeline">
          <DetailRows rows={timelineRows} />
        </DetailSection>

        <DetailSection title="Amounts">
          <DetailRows rows={amountRows} />
        </DetailSection>

        <DetailSection title="Buyer">
          <DetailRows rows={buyerRows} />
        </DetailSection>

        <DetailSection title="Product">
          <DetailRows
            rows={[
              ['Title', reservation.product?.title || '—'],
              ['Brand', reservation.product?.brand || '—'],
              ['Listing status', reservation.product?.status || '—'],
              [
                'List price',
                reservation.product?.price
                  ? formatMoney(reservation.product.price, reservation.product.currency || reservation.currency)
                  : '—',
              ],
            ]}
          />
        </DetailSection>

        {address ? (
          <DetailSection title="Shipping address">
            <DetailRows rows={addressRows} />
          </DetailSection>
        ) : null}

        {reservation.order ? (
          <DetailSection title="Linked order">
            <DetailRows
              rows={[
                ['Order #', reservation.order.order_number],
                ['Status', reservation.order.status],
                ['Payment', reservation.order.payment_status],
                [
                  'Total',
                  formatMoney(reservation.order.total_price, reservation.currency),
                ],
              ]}
            />
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: '12px' }}
              onClick={() => navigate(`/orders/${reservation.order.id}`)}
            >
              Open order
            </button>
          </DetailSection>
        ) : null}
      </div>

      {!readOnly && reservation.can_complete ? (
        <DetailSection title="Complete reservation" className="order-detail__action-card">
          <p className="order-detail__hint" style={{ marginBottom: '12px' }}>
            Use this after the remaining balance has been collected (bank transfer, in-store, etc.).
            The order will be confirmed, marked fully paid, and the product will be sold.
          </p>
          <label htmlFor="complete-note" className="order-detail__field-label">
            Note (optional)
          </label>
          <textarea
            id="complete-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="order-detail__textarea"
            style={{ marginBottom: '16px' }}
            placeholder="e.g. Balance received via bank transfer"
          />
          <button
            type="button"
            className="btn btn-primary"
            disabled={completing}
            onClick={() => void handleComplete()}
          >
            {completing ? 'Processing…' : 'Convert to confirmed order'}
          </button>
        </DetailSection>
      ) : null}

      {reservation.status === 'reserved_pending_deposit' ? (
        <p className="order-detail__hint" style={{ marginTop: '16px' }}>
          Waiting for the buyer to pay the deposit online. No action required until payment succeeds.
        </p>
      ) : null}

      {reservation.status === 'completed' ? (
        <p className="order-detail__hint" style={{ marginTop: '16px' }}>
          This reservation has been finalized.
        </p>
      ) : null}

      {!readOnly && reservation.can_delete !== false ? (
        <div
          className="order-detail__action-card"
          style={{ marginTop: '1.25rem', borderColor: '#fecaca', background: '#fff5f5' }}
        >
          <h3 className="order-detail__section-title" style={{ color: '#991b1b' }}>
            Danger zone
          </h3>
          <p className="order-detail__hint" style={{ marginBottom: '12px' }}>
            Delete this reservation record. Completed reservations must be removed via the linked order.
          </p>
          <button
            type="button"
            className="btn btn-danger"
            disabled={deleting}
            onClick={() => void handleDelete()}
          >
            {deleting ? 'Deleting…' : 'Delete reservation'}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default ReservationDetails;
